import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useJobButtonInfo} from "./hooks/useJobButtonInfo";
import {defaultAlert, defaultConfirm} from "../../../utils/alert";
import {EmptyState} from "../../../components/common/EmptyStateProps";
import {Loading} from "../../../components/common/Loading";
import {LocationSection} from "./components/LocationSection";
import {CareInfoSection} from "./components/CareInfoSection";
import {PatientInfoSection} from "./components/PatientInfoSection";
import {PayInfoSection} from "./components/PayInfoSection";
import {ActionButtons} from "./components/ActionButtons";
import {ApplyModal} from "./modals/ApplyModal";
import {CancelModal} from "./modals/CancelModal";
import {useJobDetail} from "./hooks/useJobDetail";
import {ReviewModal} from "./modals/ReviewModal";
import {RequestEndModal} from "./modals/RequestEndModal";
import {AdjustDecisionModal} from "./modals/AdjustDecisionModal";
import {JobInfoSection} from "./components/JobInfoSection";
import {usePrepaidSetting} from "../../../hooks/usePrepaidSetting";


const JobDetailPage = () => {
    const navigate = useNavigate();
    const {
        data, loading, cancelInfo, settings,
        loadCancelInfo,
        handleCancel, handleCancelReservation, handleApply,
        handleConfirmEnd, handleRequestEnd, handleAdjustDecision,
    } = useJobDetail();

    const {prepaidSetting} = usePrepaidSetting();
    const paymentFeeRate = Number(prepaidSetting?.paymentFeeRate || 0);

    const [modal, setModal] = useState({
        apply: false, cancel: false, requestEnd: false, review: false,
        adjustApprove: false, adjustReject: false,
    });
    const toggle = (key: keyof typeof modal, val: boolean) =>
        setModal(prev => ({...prev, [key]: val}));

    const buttonInfo = useJobButtonInfo(data);

    const handleAction = (action: string) => {
        switch (action) {
            case 'apply':
                return toggle('apply', true);
            case 'cancel':
                defaultConfirm('등록된 공고를 취소 하시겠습니까?', '확인', '닫기', async (ok) => {
                    if (ok) {
                        await handleCancel();
                        navigate('/announcement/matching');
                    }
                });
                break;
            case 'cancelReservation':
                if (data.isApplier) {
                    loadCancelInfo().then(() => toggle('cancel', true));
                } else {
                    defaultConfirm('예약을 취소하시겠습니까?', '확인', '닫기', (ok) => {
                        if (ok) handleCancelReservation();
                    });
                }
                break;
            case 'confirmEnd':
                if (!data.isCoordinatorConfirm) {
                    defaultAlert('매칭된 코디가 먼저 종료 신청해야 합니다.');
                    return;
                }
                toggle('review', true);
                break;
            case 'requestEnd':
                return toggle('requestEnd', true);
            case 'adjustApprove':
                return toggle('adjustApprove', true);
            case 'adjustReject':
                return toggle('adjustReject', true);
        }
    };

    // 공고 재등록 조건: 작성자 + 모집중 + 지원코디 0명
    const canReRegister = data?.isApplier && data?.status === 'RECRUITING' && (data?.coordinatorCount || 0) === 0;

    const handleReRegister = () => {
        const serviceTypeMap: { [key: string]: string } = {
            'CARE': '/service/apply/care',
            'ESCORT': '/service/apply/escort',
            'HOUSEWORK': '/service/apply/housekeeping',
        };
        const path = serviceTypeMap[data.serviceType];
        if (!path) {
            defaultAlert('서비스 유형을 확인할 수 없습니다.');
            return;
        }
        navigate(path, { state: { reRegisterData: data } });
    };

    // 공고 작성자가 아니면 마스킹
    const shouldMask = !data?.isApplier;

    if (!data) return <EmptyState message="공고 정보를 찾을 수 없습니다."/>;

    return (
        <>
            <Loading loading={loading} onClose={() => {
            }}/>

            <JobInfoSection data={data} showReRegister={canReRegister} onReRegister={handleReRegister} maskAddress={shouldMask}/>
            <LocationSection data={data} maskAddress={shouldMask}/>
            <CareInfoSection data={data}/>
            <PatientInfoSection data={data} maskPatientName={shouldMask}/>
            <PayInfoSection data={data}/>

            <ActionButtons buttonInfo={buttonInfo} onAction={handleAction}/>

                <ApplyModal
                    isOpen={modal.apply}
                    onClose={() => toggle('apply', false)}
                    onConfirm={async (salary) => {
                        await handleApply(salary);
                        toggle('apply', false);
                    }}
                    currentSalary={data.additionalPay || data.dailyPay}
                    totalDays={data.totalDays || 0}
                    negotiable={data.negotiable === '협의가능'}
                    workType={data.workType}
                    workStartTime={data.workStartTime}
                    workEndTime={data.workEndTime}
                    proposalRangeMinus={settings?.proposalRangeMinus ?? 0}
                    proposalRangePlus={settings?.proposalRangePlus ?? 0}
                />
                <CancelModal
                    isOpen={modal.cancel}
                    onClose={() => toggle('cancel', false)}
                    onConfirm={async () => {
                        await handleCancelReservation();
                        toggle('cancel', false);
                    }}
                    cancelInfo={cancelInfo}
                />
                <RequestEndModal
                    isOpen={modal.requestEnd}
                    onClose={() => toggle('requestEnd', false)}
                    onConfirm={async (...args: Parameters<typeof handleRequestEnd>) => {
                        await handleRequestEnd(...args);
                        toggle('requestEnd', false);
                    }}
                    currentTotalPay={(() => {
                        const base = data.additionalPay || data.dailyPay || 0;
                        const days = data.totalDays || 0;
                        if (data.workType === 'HOURLY' && data.workStartTime && data.workEndTime) {
                            const [sH, sM] = data.workStartTime.split(':').map(Number);
                            const [eH, eM] = data.workEndTime.split(':').map(Number);
                            const hours = (eH * 60 + eM - (sH * 60 + sM)) / 60;
                            return base * hours * days;
                        }
                        return base * days;
                    })()}
                />
                <ReviewModal
                    isOpen={modal.review}
                    onClose={() => toggle('review', false)}
                    onConfirm={async (r, c) => {
                        await handleConfirmEnd(r, c);
                        toggle('review', false);
                    }}
                />
                <AdjustDecisionModal
                    isOpen={modal.adjustApprove}
                    type="approve"
                    onClose={() => toggle('adjustApprove', false)}
                    onConfirm={() => {
                        toggle('adjustApprove', false);
                        toggle('review', true);
                    }}
                    adjustDirection={data.adjustDirection || 'add'}
                    adjustAmount={data.adjustAmount || 0}
                    paymentFeeRate={paymentFeeRate}
                    currentTotalPay={(() => {
                        const base = data.additionalPay || data.dailyPay || 0;
                        const days = data.totalDays || 0;
                        if (data.workType === 'HOURLY' && data.workStartTime && data.workEndTime) {
                            const [sH, sM] = data.workStartTime.split(':').map(Number);
                            const [eH, eM] = data.workEndTime.split(':').map(Number);
                            const hours = (eH * 60 + eM - (sH * 60 + sM)) / 60;
                            return base * hours * days;
                        }
                        return base * days;
                    })()}
                />
                <AdjustDecisionModal
                    isOpen={modal.adjustReject}
                    type="reject"
                    onClose={() => toggle('adjustReject', false)}
                    onConfirm={async () => {
                        toggle('adjustReject', false);
                        await handleAdjustDecision(false);
                    }}
                    adjustDirection={data.adjustDirection || 'add'}
                    adjustAmount={data.adjustAmount || 0}
                    paymentFeeRate={paymentFeeRate}
                    currentTotalPay={(() => {
                        const base = data.additionalPay || data.dailyPay || 0;
                        const days = data.totalDays || 0;
                        if (data.workType === 'HOURLY' && data.workStartTime && data.workEndTime) {
                            const [sH, sM] = data.workStartTime.split(':').map(Number);
                            const [eH, eM] = data.workEndTime.split(':').map(Number);
                            const hours = (eH * 60 + eM - (sH * 60 + sM)) / 60;
                            return base * hours * days;
                        }
                        return base * days;
                    })()}
                />
        </>
    );
};

export default JobDetailPage;